import React, { useContext } from 'react';
import cx from 'classnames'
import { MediaQueryContext, MediaQueryResults } from '@arrive/responsive-provider';

import {
  getFormattedAppointmentDate
} from 'helpers/loadDetailHelpers';
import { FullPriceFormatter, PricingFormatter } from 'helpers/formatters';

import Modal, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalCloseButton
} from '@arrive/modal';
import LoadingSpinner from '@arrive/loading-spinner';
import SVGIcon from '@arrive/svg-icon';

import { BOOK_FAILURE, BOOK_LOCK_FAILURE, TENDER_SUCCESS } from 'constants/bookingConstants';
import ORIGIN from '@arrive/ui-icons/svg/ic_up_right_arrow.svg';
import DESTINATION from '@arrive/ui-icons/svg/ic_down_right_arrow.svg';
import X_MARK from '@arrive/ui-icons/svg/ic_close_fill.svg'
import CHECK_MARK from '@arrive/ui-icons/svg/ic_circle_check.svg'

import { LoadType } from 'sharedTypes/loads/loadType';
import { OfferType } from 'sharedTypes/offers/offers';

import styles from 'components/CommonModalComponents/CommonModalStyles.scss';

/* Booking Specific Reusable Components */
export interface BookingHeaderProps {
  loadDetails: LoadType;
  offer: OfferType;
  closeModal: () => void;
  isMAO: boolean;
  headerText?: string;
  isAcceptAndBook?: boolean;
}

export const BookingHeaderSection = ({ headerText, loadDetails, offer, closeModal, isMAO, isAcceptAndBook }: BookingHeaderProps) => {
  const offerPriceToShow = offer && isAcceptAndBook ? offer.bizCounterAmount : offer?.amount;

  const bookingPrice = offer ? offerPriceToShow : loadDetails.TopSpend;

  return (
    <ModalHeader
      className={styles.headerBar}
      title={headerText}
    >
      <div data-testid={"bookingModalHeader"}>
        {!isMAO && <span data-testid={"bookingModalPrice"}>{bookingPrice % 1 ? FullPriceFormatter.format(bookingPrice) : PricingFormatter.format(bookingPrice)}</span>}
        <ModalCloseButton className={styles.closeButton} onClick={closeModal} />
      </div>
    </ModalHeader>
  )
};

/* Base Components */
interface CommonModalWrapperProps {
  children: React.ReactNode;
  setShowModal: () => void;
  loading: boolean;
}

export const CommonModalWrapper = ({ children, setShowModal, loading }: CommonModalWrapperProps) => {
  const currentMedia = useContext(MediaQueryContext);

  const stopPropagationXs = (currentMediaTypes: MediaQueryResults, event: React.MouseEvent) => {
    if (currentMediaTypes.xs) {
      event.stopPropagation();
    }
  }
  return (
    <div onClick={(e) => stopPropagationXs(currentMedia, e)} >
      <Modal className={styles.commonModal} setShowModal={setShowModal} closeOnEsc={!loading}>
        {children}
      </Modal>
    </div>
  );
}

export interface CommonModalBodyProps {
  children: React.ReactNode;
  preventBodyScroll?: boolean;
  className?: string;
}

export const CommonModalBody = ({ children, className, ...rest }: CommonModalBodyProps) => {
  return (
    <ModalBody className={cx(styles.listContainer, className)} {...rest}>
      {children}
    </ModalBody>
  );
};

export const CommonModalSectionContainer = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className={styles.sectionContainer}>
      {children}
    </div>
  )
};

interface CommonModalSectionIconProps {
  number: number;
  isVisible: boolean;
  isValid: boolean | null; // Null represents the neutral state
}

export const CommonModalSectionIcon = ({ number, isVisible, isValid }: CommonModalSectionIconProps) => {
  const sectionIconSvgRef = isValid === true ? CHECK_MARK : X_MARK;
  return (
    isValid === null ? <div className={cx(styles.sectionNumber, !isVisible && styles.disabled)}>{number}</div> : <SVGIcon svgRef={sectionIconSvgRef} className={cx(styles.sectionIcon, !isValid && styles.error)} />
  )
};

/* End Components */

export interface CommonModalStatusReadbackProps {
  headerMessage: string;
  binStatus: string;
  icon: {
    id: string;
    viewBox: string;
  }
  actionMessage: React.ReactNode;
}

export function CommonModalStatusReadback({ headerMessage, binStatus, icon, actionMessage }: CommonModalStatusReadbackProps) {
  return (
    <ModalBody className={styles.errorContainer}>
      <h3 className={styles.errorTitle}>{headerMessage}</h3>
      <SVGIcon svgRef={icon} className={cx(styles.icon, binStatus === BOOK_FAILURE && styles.errorIcon, binStatus === BOOK_LOCK_FAILURE && styles.warningIcon, binStatus === TENDER_SUCCESS && styles.tenderIcon)} />
      <h4 className={styles.whatsNextHeader}>Whats Next?</h4>
      <div className={styles.errorMessage}>{actionMessage}</div>
    </ModalBody>
  )
}

export function CommonModalLoadSaveSpinner({ savingText }: { savingText: string }) {
  return (
    <ModalBody className={styles.savingStateContainer}>
      <h3 className={styles.hangTightText}>Hang Tight!</h3>
      <LoadingSpinner
        savingText={savingText || ''}
      />
    </ModalBody>
  )
}

interface CommonModalFooterWrapperProps {
  children: React.ReactNode;
}

export const CommonModalFooterWrapper = ({ children }: CommonModalFooterWrapperProps) => {
  return (
    <ModalFooter className={styles.footerBar}>
      {children}
    </ModalFooter>
  )
}

interface LocationDetailProps {
  city: string;
  state: string;
  earlyTime: string;
  lateTime: string;
  timezone: string;
  icon: { id: string; viewBox: string };
}

const LocationDetail = ({ city, state, earlyTime, lateTime, timezone, icon }: LocationDetailProps) => {
  const content = (
    <>
      <SVGIcon className={styles.locationIcon} svgRef={icon} />
      <div>
        <h3 className={styles.city}>{city?.toLowerCase()}, {state}</h3>
        <h5 className={styles.time}>{getFormattedAppointmentDate(earlyTime, lateTime, timezone, true)}</h5>
      </div>
    </>
  )

  return content;
}

export function CommonModalLocationReadback({ load = {} as LoadType, offer = {} as OfferType }: { load?: LoadType; offer?: OfferType }) {
  return (
    <div className={styles.locationTiles}>
      <div className={styles.location}>
        <LocationDetail
          icon={ORIGIN}
          city={load.PickupEarlyCity || offer.originCity}
          state={load.PickupEarlyStateCode || offer.originState}
          timezone={load.PickupLocationIANACode || offer.originCityIANATimeZoneName}
          earlyTime={load.PickupApptEarliest || offer.originEarliest}
          lateTime={load.PickupApptLatest || offer.originLatest}
        />
      </div>
      <div className={styles.location}>
        <LocationDetail
          icon={DESTINATION}
          city={load.DeliveryLateCity || offer.destinationCity}
          state={load.DeliveryLateStateCode || offer.destinationState}
          timezone={load.DeliveryLocationIANACode || offer.destinationCityIANATimeZoneName}
          earlyTime={load.DeliveryApptEarliest || offer.destinationEarliest}
          lateTime={load.DeliveryApptLatest || offer.destinationLatest}
        />
      </div>
    </div>)
}
